// Track 2.3 — sustained-position segmenter.
//
// Watches the position class + smoothed CdA stream and detects when the
// rider has held a single position for at least MIN_HOLD_SEC seconds.
// Returns one PositionHold per detected hold, plus a comparison snapshot
// whenever two or more distinct positions have been held in the current
// session.
//
// The rider sees a side-by-side card: "Position A: 0.241 m². Position B:
// 0.226 m². You save 18 W at 40 km/h in B." Pure aggregation on top of
// the existing position state machine — no new physics.

// Wave 20.44 — lowered from 30 → 15 s. Rider had a 24-second
// aero session (baseline lock + brief position hold) and saw an
// empty aero pane because nothing crossed the old 30 s bar.
// 15 s is still long enough to filter out casual movement.
const MIN_HOLD_SEC = 15;       // a position has to be held this long to count
const STABILITY_PCT = 0.04;     // CdA within ±4% of mean during hold to be "clean"
const MAX_HOLDS = 16;           // cap memory; session-end report will see all of them

export interface PositionHold {
  /** Class label — drops, hoods, etc. */
  positionClass: string;
  /** Mean CdA during the hold (m²). */
  cdaMean: number;
  /** Best (lowest) CdA observed during the hold. */
  cdaBest: number;
  /** Duration the position was held (seconds). */
  durationSec: number;
  /** ISO timestamp the rider entered this hold. */
  enteredAt: string;
}

export interface ABComparison {
  /** The most recently completed hold. */
  current: PositionHold;
  /** The best (lowest-CdA) hold seen so far in this session. */
  best: PositionHold;
  /** Watts saved at 40 km/h (positive when current is better than best so far). */
  wattsDelta: number;
}

export class PositionSegmenter {
  private holds: PositionHold[] = [];

  // Running aggregation of the *current* in-progress hold.
  private cur: {
    cls: string;
    startSec: number;
    sum: number;
    count: number;
    best: number;
  } | null = null;

  /** Feed each frame. Returns a freshly-completed hold (when the rider
   *  switches position after holding long enough) and the running
   *  comparison snapshot, otherwise nulls.
   *
   *  Wave 20.49l — `transitioning` is true while the classifier is
   *  mid-hysteresis (raw class differs from confirmed class but the
   *  frame count hasn't reached HYSTERESIS_FRAMES yet). During that
   *  window, the CdA the rider is producing likely belongs to the
   *  NEXT position, not the currently-labeled one — so we drop the
   *  sample entirely rather than credit it to the wrong bucket. */
  update(
    positionClass: string,
    smoothedCda: number | null,
    tSec: number,
    transitioning: boolean = false,
  ): { completedHold: PositionHold | null; ab: ABComparison | null } {
    if (
      positionClass === 'unknown' ||
      smoothedCda == null ||
      !Number.isFinite(smoothedCda) ||
      smoothedCda <= 0 ||
      transitioning
    ) {
      // Treat as "no signal" — don't end the hold, don't extend it.
      return { completedHold: null, ab: null };
    }

    // Start tracking the first position we see.
    if (!this.cur) {
      this.cur = {
        cls: positionClass,
        startSec: tSec,
        sum: smoothedCda,
        count: 1,
        best: smoothedCda,
      };
      return { completedHold: null, ab: null };
    }

    // Same class — extend the current hold's stats.
    if (positionClass === this.cur.cls) {
      this.cur.sum += smoothedCda;
      this.cur.count += 1;
      if (smoothedCda < this.cur.best) this.cur.best = smoothedCda;
      return { completedHold: null, ab: null };
    }

    // Class changed — finalise the previous hold if it was long enough +
    // stable enough, then start a new one.
    const dur = tSec - this.cur.startSec;
    const mean = this.cur.sum / this.cur.count;
    let completedHold: PositionHold | null = null;
    if (dur >= MIN_HOLD_SEC) {
      // Stability check: only record if the running mean didn't bounce
      // around too much. (Best vs mean within tolerance is a cheap proxy.)
      const dev = Math.abs(this.cur.best - mean) / Math.max(mean, 1e-6);
      if (dev <= STABILITY_PCT * 4) {
        completedHold = {
          positionClass: this.cur.cls,
          cdaMean: mean,
          cdaBest: this.cur.best,
          durationSec: dur,
          enteredAt: new Date(Date.now() - dur * 1000).toISOString(),
        };
        this.holds.push(completedHold);
        if (this.holds.length > MAX_HOLDS) this.holds.shift();
      }
    }

    this.cur = {
      cls: positionClass,
      startSec: tSec,
      sum: smoothedCda,
      count: 1,
      best: smoothedCda,
    };

    // Build an A/B comparison if we have at least two completed holds.
    let ab: ABComparison | null = null;
    if (completedHold && this.holds.length >= 2) {
      let best = this.holds[0];
      for (const h of this.holds) {
        if (h.cdaBest < best.cdaBest) best = h;
      }
      // Skip when current *is* the best — that's the celebratory case for
      // Track 3.1's best_cda event, not the A/B card.
      if (best !== completedHold) {
        // P_drag = 0.5·ρ·CdA·v³ at 40 km/h (11.11 m/s).
        // Watts delta is positive when current (the new hold) is *worse* —
        // we report how much the rider gains by going back to best.
        const wattsDelta =
          0.5 * 1.225 * (completedHold.cdaBest - best.cdaBest) * 11.11 ** 3;
        ab = { current: completedHold, best, wattsDelta };
      }
    }
    return { completedHold, ab };
  }

  /** All completed holds so far this session. Used by Track 5.2 to render
   *  the session-end position ranking. */
  allHolds(): PositionHold[] {
    return [...this.holds];
  }

  /** Wave 20.44 — snapshot of the in-progress hold. Used by the
   *  session guide to show a "recording drops · 12/30s" countdown
   *  so the rider knows the segmenter is watching. Returns null
   *  when no class is being tracked. */
  currentHold(nowSec: number): {
    positionClass: string;
    elapsedSec: number;
    minRequiredSec: number;
  } | null {
    if (!this.cur) return null;
    return {
      positionClass: this.cur.cls,
      elapsedSec: Math.max(0, nowSec - this.cur.startSec),
      minRequiredSec: MIN_HOLD_SEC,
    };
  }

  /** Wave 20.44 — force-finalize the in-progress hold. Prior to
   *  this fix, a rider who held ONE position for the whole session
   *  and never switched saw pos_count=0 on their summary because
   *  completion only fired on a class transition. Called from
   *  session-end so short sessions still record what the rider
   *  actually did. Returns the new hold (if it qualified) or null.
   *
   *  Unlike the update() path, finish() SKIPS the stability check
   *  — end-of-session records the rider's actual effort even if
   *  their CdA drifted (settling behavior early in a session is
   *  the norm, not a reason to hide the hold entirely). */
  finish(nowSec: number): PositionHold | null {
    if (!this.cur) return null;
    const dur = nowSec - this.cur.startSec;
    if (dur < MIN_HOLD_SEC) return null;
    const mean = this.cur.sum / this.cur.count;
    const hold: PositionHold = {
      positionClass: this.cur.cls,
      cdaMean: mean,
      cdaBest: this.cur.best,
      durationSec: dur,
      enteredAt: new Date(Date.now() - dur * 1000).toISOString(),
    };
    this.holds.push(hold);
    if (this.holds.length > MAX_HOLDS) this.holds.shift();
    // Clear so a repeat finish() call is a no-op.
    this.cur = null;
    return hold;
  }

  reset(): void {
    this.holds = [];
    this.cur = null;
  }
}
